// Libs
import React, { useState, useCallback, useEffect } from "react";
import {
  PhotoIcon,
  TrashIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

// Local
import { ExistingMarketingAttachment } from "../../models/marketing-attachment";
import { DbRead, DbWrite } from "../../database";
import { useToastMessageStore } from "../../store/toast-messages";

interface Props {
  siteKey: string;
}

export default function StandaloneEmailImagesManager({ siteKey }: Props) {
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );

  const [images, setImages] = useState<ExistingMarketingAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [previewImage, setPreviewImage] =
    useState<ExistingMarketingAttachment | null>(null);

  // Load images when component mounts
  useEffect(() => {
    if (siteKey) {
      loadImages();
    }
  }, [siteKey]);

  const loadImages = useCallback(async () => {
    if (!siteKey) return;

    setLoading(true);
    try {
      const emailImages = await DbRead.marketingAttachments.getAll(siteKey);
      setImages(emailImages);
    } catch (error) {
      console.error("Failed to load email images:", error);
      setUploadError("Failed to load images. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [siteKey]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Validate file types
      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      const invalidFiles = Array.from(files).filter(
        (file) => !validTypes.includes(file.type),
      );

      if (invalidFiles.length > 0) {
        setUploadError(
          "Please select only image files (JPEG, PNG, GIF, WebP).",
        );
        return;
      }

      // Validate file sizes (max 10MB each)
      const oversizedFiles = Array.from(files).filter(
        (file) => file.size > 10 * 1024 * 1024,
      );
      if (oversizedFiles.length > 0) {
        setUploadError("Each image must be smaller than 10MB.");
        return;
      }

      setSelectedFiles(files);
      setUploadError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles || !siteKey) return;

    setUploading(true);
    setUploadError(null);

    try {
      for (const file of Array.from(selectedFiles)) {
        await DbWrite.marketingAttachments.upload({
          siteKey: siteKey,
          file,
          alt: file.name,
          description: `Uploaded marketing image: ${file.name}`,
        });
      }

      setSuccessMessage(
        `Successfully uploaded ${selectedFiles.length} image(s)!`,
      );
      addToastMessage({
        id: `images-uploaded-${Date.now()}`,
        message: `Successfully uploaded ${selectedFiles.length} image(s)!`,
        type: "success",
        dialog: false,
      });

      setSelectedFiles(null);

      // Clear the file input
      const fileInput = document.getElementById(
        "standalone-image-upload",
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }

      // Reload images
      await loadImages();
    } catch (error) {
      console.error("Failed to upload images:", error);
      setUploadError("Failed to upload images. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (image: ExistingMarketingAttachment) => {
    if (!siteKey) return;

    if (
      !window.confirm(`Are you sure you want to delete "${image.filename}"?`)
    ) {
      return;
    }

    try {
      await DbWrite.marketingAttachments.delete({
        siteKey: siteKey,
        attachmentId: image.id,
      });

      setSuccessMessage("Image deleted successfully!");
      addToastMessage({
        id: `image-deleted-${Date.now()}`,
        message: "Image deleted successfully!",
        type: "success",
        dialog: false,
      });

      await loadImages();
    } catch (error) {
      console.error("Failed to delete image:", error);
      setUploadError("Failed to delete image. Please try again.");
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setSuccessMessage("Image URL copied to clipboard!");
        addToastMessage({
          id: `url-copied-${Date.now()}`,
          message: "Image URL copied to clipboard!",
          type: "success",
          dialog: false,
        });
      })
      .catch(() => {
        setUploadError("Failed to copy URL to clipboard.");
      });
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="mb-4 text-lg font-medium text-gray-900">
          Upload New Images
        </h3>

        <div className="mb-4">
          <input
            type="file"
            id="standalone-image-upload"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-full file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-blue-700 hover:file:bg-blue-100"
          />
          <p className="mt-1 text-sm text-gray-500">
            PNG, JPG, GIF, WebP up to 10MB each
          </p>
        </div>

        {selectedFiles && (
          <div className="mb-4">
            <p className="text-sm text-gray-700">
              Selected {selectedFiles.length} file(s)
            </p>
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="mt-2 inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <PlusIcon className="mr-2 h-4 w-4" />
                  Upload Images
                </>
              )}
            </button>
          </div>
        )}

        {/* Error/Success Messages */}
        {uploadError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-600">{uploadError}</p>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3">
            <p className="text-sm text-green-600">{successMessage}</p>
          </div>
        )}
      </div>

      {/* Images Grid */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="mb-4 text-lg font-medium text-gray-900">
          Your Images ({images.length})
        </h3>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : images.length === 0 ? (
          <div className="py-8 text-center">
            <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h4 className="mt-2 text-sm font-medium text-gray-900">
              No images yet
            </h4>
            <p className="mt-1 text-sm text-gray-500">
              Upload your first marketing image to get started.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {images.map((image) => (
              <div
                key={image.id}
                className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="aspect-square overflow-hidden bg-gray-100">
                  <img
                    src={image.url}
                    alt={image.filename}
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                </div>

                <div className="p-3">
                  <h4 className="truncate text-sm font-medium text-gray-900">
                    {image.filename}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {new Date(
                      image.timestampCreated.toDate(),
                    ).toLocaleDateString()}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 transition-all group-hover:bg-opacity-50">
                  <div className="flex space-x-2 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      onClick={() => handleCopyUrl(image.url)}
                      className="rounded-full bg-white p-2 text-gray-700 shadow-sm hover:bg-gray-50"
                      title="Copy URL"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => setPreviewImage(image)}
                      className="rounded-full bg-white p-2 text-gray-700 shadow-sm hover:bg-gray-50"
                      title="Preview"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => handleDeleteImage(image)}
                      className="rounded-full bg-white p-2 text-red-600 shadow-sm hover:bg-red-50"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="relative max-h-[90vh] max-w-[90vw] rounded-lg bg-white p-4">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute right-2 top-2 rounded-full bg-gray-100 p-2 text-gray-600 hover:bg-gray-200"
            >
              ✕
            </button>
            <img
              src={previewImage.url}
              alt={previewImage.filename}
              className="max-h-[80vh] max-w-full rounded"
            />
            <div className="mt-4 text-center">
              <h3 className="font-medium text-gray-900">
                {previewImage.filename}
              </h3>
              <p className="text-sm text-gray-500">{previewImage.url}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
